import { getDaysInMonth } from 'date-fns';
import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { string } from 'yup';

import { TSerialisedPeople } from './PersonModal.types';
import { getPeopleWithGuarantorStatus } from '../../../api/lendingAPI/CustomerStateAPIHelper';

const messages = defineMessages({
    validNameRequired: {
        id: '_.person.error.valid_name_required',
        defaultMessage: 'Please enter a valid name'
    },
    validDateOfBirthRequired: {
        id: '_.person.error.valid_date_of_birth_required',
        defaultMessage: 'Please enter a valid date of birth'
    },
    emailAddressRequired: {
        id: '_.person.error.email_address_required',
        defaultMessage: 'Please enter an email address'
    },
    phoneNumberRequired: {
        id: '_.person.error.phone_number_required',
        defaultMessage: 'Please enter at least one phone number'
    },
    postcodeRequired: {
        id: '_.person.error.postcode_required',
        defaultMessage: 'Please enter a postcode'
    },
    townRequired: {
        id: '_.person.error.town_required',
        defaultMessage: 'Please enter a town'
    },
    differentPhoneNumberRequired: {
        id: '_.person.error.different_phone_number_required',
        defaultMessage: 'Phone number cannot be the same as other guarantors'
    },
    differentEmailRequired: {
        id: '_.person.error.different_email_required',
        defaultMessage: 'Email cannot be the same as other guarantors'
    },
    addressRequired: {
        id: '_.person.error.address_required',
        defaultMessage: 'Please enter at least one address'
    },
    consentForAdditionalGuarantorRequired: {
        id: '_.person.error.consent_for_additional_guarantor_required',
        defaultMessage: 'Consent required'
    }
});

const emailSchema = string().email();

type ValidationContext = {
    intl: IntlShape;
    people: TSerialisedPeople;
    uid: string | undefined;
    country: string;
};

type ValidationErrors = Record<string, { type: string; message: string }>;

export function validatePersonForm(
    values: Record<string, any>,
    context: ValidationContext
): ValidationErrors {
    const { intl, people, uid, country } = context;
    const guarantorStatus = getPeopleWithGuarantorStatus(people, uid || '');
    const errors: ValidationErrors = {};

    function addError(
        path: string,
        msg: { id: string; defaultMessage: string }
    ) {
        errors[path] = { type: 'validate', message: intl.formatMessage(msg) };
    }

    if (!values.firstName) {
        addError('firstName', messages.validNameRequired);
    }

    if (!values.lastName) {
        addError('lastName', messages.validNameRequired);
    }

    const day = values?.dateOfBirth?.day;
    const month = values?.dateOfBirth?.month;
    const year = values?.dateOfBirth?.year;

    if (!day || isNaN(day)) {
        addError('dateOfBirth.day', messages.validDateOfBirthRequired);
    }

    if (!month) {
        addError('dateOfBirth.month', messages.validDateOfBirthRequired);
    }

    if (!year || String(year).length !== 4 || isNaN(year)) {
        addError('dateOfBirth.year', messages.validDateOfBirthRequired);
    }

    if (
        day &&
        month &&
        year &&
        String(year).length === 4 &&
        !isNaN(year) &&
        (day <= 0 || day > getDaysInMonth(new Date(`${year}-${month}-01`)))
    ) {
        addError('dateOfBirth.day', messages.validDateOfBirthRequired);
    }

    if (!values.email) {
        addError('email', messages.emailAddressRequired);
    } else if (!emailSchema.isValidSync(values.email)) {
        addError('email', messages.emailAddressRequired);
    } else if (guarantorStatus.email.some((e) => e?.email === values.email)) {
        addError('email', messages.differentEmailRequired);
    }

    if (!values.phones?.[0]?.number) {
        addError('phones.0.number', messages.phoneNumberRequired);
    }

    values.phones?.forEach((phone: { number?: string }, index: number) => {
        if (phone?.number) {
            const found = guarantorStatus.phones.some(
                (p) => p?.number === phone.number
            );
            if (found) {
                addError(
                    `phones.${index}.number`,
                    messages.differentPhoneNumberRequired
                );
            }
        }
    });

    if (!values.addresses || !values.addresses.length) {
        addError('addresses', messages.addressRequired);
    }

    const primaryAddress = values.addresses?.[0];
    if (primaryAddress) {
        if (!primaryAddress.postcode) {
            addError('addresses.0.postcode', messages.postcodeRequired);
        } else if (
            country === 'DE' &&
            !/^\d{5}$/.test(primaryAddress.postcode)
        ) {
            addError('addresses.0.postcode', messages.postcodeRequired);
        }
        if (!primaryAddress.town) {
            addError('addresses.0.town', messages.townRequired);
        }
    }

    if (
        values.isGuarantor === true &&
        values.additionalGuarantorConsent !== true
    ) {
        addError(
            'additionalGuarantorConsent',
            messages.consentForAdditionalGuarantorRequired
        );
    }

    return errors;
}
